/**
 * Branding settings (BR-2) — per-workspace brand shown on guest share pages.
 *
 * Built 2026-08-24 (replaces the "Soon" placeholder). Owner-only (sidebar item
 * is access:"owner"; server gates PUT via ACTIONS.CHANGE_WORKSPACE_SETTINGS).
 * Backed by workspace_settings brand_* columns (migration 0021). Scope: studio
 * name + logo + accent color. Guest pages keep a "Powered by Editlock" footer.
 */

import { useEffect, useRef, useState } from "react";
import { Check, ImageIcon, Loader2, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SettingsPageHeader } from "@/pages/settings/settings-placeholder";
import {
  useWorkspaceSettings,
  useUpdateWorkspaceSettings,
  useUploadBrandLogo,
  type WorkspaceSettingsUpdate,
} from "@/hooks/use-workspace-settings";
import { authErrorMessage } from "@/lib/auth-errors";
import { FeatureUpgradeBanner } from "@/components/billing/feature-lock";
import { useHasFeature } from "@/hooks/use-plan";

const LOGO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const LOGO_MAX_BYTES = 10 * 1024 * 1024;
const DEFAULT_ACCENT_SWATCH = "#8b5cf6";

export function BrandingSettingsPage() {
  const { data, isLoading, isError } = useWorkspaceSettings();
  const update = useUpdateWorkspaceSettings();
  const upload = useUploadBrandLogo();
  const fileInput = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [accent, setAccent] = useState("");           // "" = use default
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  // undefined = unchanged, string = new key to commit, null = remove.
  const [pendingLogoKey, setPendingLogoKey] = useState<string | null | undefined>(undefined);
  const [pendingLogoBytes, setPendingLogoBytes] = useState<number | undefined>(undefined);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!data) return;
    setName(data.brandName ?? "");
    setAccent(data.brandAccent ?? "");
    setLogoUrl(data.brandLogoUrl);
    setPendingLogoKey(undefined);
  }, [data]);

  const canEdit = data?.canEdit ?? false;
  // Custom branding is a Studio+ feature. Below it, the section is read-only and
  // shows an upgrade banner (server also 403s the PUT + logo presign).
  const canBranding = useHasFeature("customBranding");
  const editable = canEdit && canBranding;

  const dirty = data
    ? (name.trim() || null) !== (data.brandName ?? null) ||
      (accent || null) !== (data.brandAccent ?? null) ||
      pendingLogoKey !== undefined
    : false;

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setLogoError(null);
    if (!LOGO_TYPES.includes(file.type)) {
      setLogoError("Logo must be a JPG, PNG, or WebP image.");
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setLogoError("Logo must be 10MB or smaller.");
      return;
    }
    try {
      const key = await upload.mutateAsync(file);
      setPendingLogoKey(key);
      setPendingLogoBytes(file.size);
      setLogoUrl(URL.createObjectURL(file));
    } catch (err) {
      setLogoError(authErrorMessage(err));
    }
  }

  function removeLogo() {
    setPendingLogoKey(null);
    setPendingLogoBytes(undefined);
    setLogoUrl(null);
    setLogoError(null);
  }

  async function handleSave() {
    setSaved(false);
    const body: WorkspaceSettingsUpdate = {
      brandName: name.trim() || null,
      brandAccent: accent || null,
    };
    if (pendingLogoKey !== undefined) {
      body.brandLogoKey = pendingLogoKey;
      if (pendingLogoKey && pendingLogoBytes != null) body.brandLogoBytes = pendingLogoBytes;
    }
    try {
      await update.mutateAsync(body);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      /* surfaced below */
    }
  }

  if (isLoading) {
    return (
      <div>
        <SettingsPageHeader title="Branding" subtitle="How your workspace looks to guests on a share link." />
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[62px] animate-pulse rounded-xl border border-border bg-secondary/30" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div>
        <SettingsPageHeader title="Branding" subtitle="How your workspace looks to guests on a share link." />
        <div className="rounded-xl border border-border bg-secondary/30 px-4 py-3 text-sm text-muted-foreground">
          Couldn't load your branding settings. Refresh to try again.
        </div>
      </div>
    );
  }

  const previewName = name.trim() || "Editlock";
  const previewLogo = logoUrl || "/logo.png";
  const accentStyle = accent || "hsl(var(--primary))";

  return (
    <div>
      <SettingsPageHeader title="Branding" subtitle="How your workspace looks to guests on a share link." />

      <FeatureUpgradeBanner feature="customBranding" title="Custom-branded share pages" />

      {canBranding && !canEdit && (
        <div className="mb-6 rounded-xl border border-border bg-secondary/30 px-4 py-3 text-sm text-muted-foreground">
          Only the workspace owner can change branding.
        </div>
      )}

      {/* Studio name */}
      <div className="rounded-xl border border-border bg-secondary/30 px-4 py-3">
        <label htmlFor="brand-name" className="text-sm font-medium text-foreground">Studio name</label>
        <p className="mb-2 mt-0.5 text-sm text-muted-foreground">
          Shown in the header of your share pages. Leave empty to show "Editlock".
        </p>
        <Input
          id="brand-name"
          value={name}
          disabled={!editable}
          maxLength={100}
          placeholder="e.g. Northlight Studio"
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      {/* Logo */}
      <div className="mt-3 rounded-xl border border-border bg-secondary/30 px-4 py-3">
        <div className="text-sm font-medium text-foreground">Logo</div>
        <p className="mb-3 mt-0.5 text-sm text-muted-foreground">JPG, PNG, or WebP, up to 10MB. Square works best.</p>
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-background">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo preview" className="h-full w-full object-contain" />
            ) : (
              <ImageIcon className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPickFile} />
            <Button variant="outline" size="sm" disabled={!editable || upload.isPending} onClick={() => fileInput.current?.click()}>
              {upload.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {upload.isPending ? "Uploading…" : logoUrl ? "Replace" : "Upload logo"}
            </Button>
            {logoUrl && editable && (
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={removeLogo}>
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </Button>
            )}
          </div>
        </div>
        {logoError && <p className="mt-2 text-xs text-destructive">{logoError}</p>}
      </div>

      {/* Accent color */}
      <div className="mt-3 rounded-xl border border-border bg-secondary/30 px-4 py-3">
        <div className="text-sm font-medium text-foreground">Accent color</div>
        <p className="mb-3 mt-0.5 text-sm text-muted-foreground">Used for buttons and links on your share pages.</p>
        <div className="flex items-center gap-3">
          <input
            type="color"
            aria-label="Accent color"
            disabled={!editable}
            value={accent || DEFAULT_ACCENT_SWATCH}
            onChange={(e) => setAccent(e.target.value.toLowerCase())}
            className="h-9 w-12 cursor-pointer rounded-md border border-border bg-background disabled:opacity-50"
          />
          <Input
            value={accent}
            disabled={!editable}
            placeholder="Using default"
            onChange={(e) => setAccent(e.target.value.trim().toLowerCase())}
            className="max-w-[160px] font-mono"
          />
          {accent && editable && (
            <Button variant="ghost" size="sm" onClick={() => setAccent("")}>Reset to default</Button>
          )}
        </div>
      </div>

      {/* Live preview */}
      <div className="mt-8">
        <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Preview</div>
        <div className="overflow-hidden rounded-xl border border-border">
          <div className="flex items-center justify-between border-b border-border bg-background px-4 py-3">
            <div className="flex items-center gap-2.5">
              <img src={previewLogo} alt="" aria-hidden className="h-7 w-7 rounded object-contain" />
              <span className="font-display text-lg font-bold tracking-tight">{previewName}</span>
            </div>
            <button
              type="button"
              className="rounded-md px-3 py-1.5 text-sm font-medium text-white"
              style={{ backgroundColor: accentStyle }}
            >
              Approve
            </button>
          </div>
          <div className="bg-secondary/20 px-4 py-6 text-center text-xs text-muted-foreground">
            Powered by Editlock
          </div>
        </div>
      </div>

      {/* Save bar */}
      {editable && (
        <div className="mt-8 flex items-center gap-3">
          <Button onClick={handleSave} disabled={!dirty || update.isPending}>
            {update.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
              </>
            ) : (
              "Save changes"
            )}
          </Button>
          {saved && !dirty && (
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <Check className="h-3.5 w-3.5 text-primary" /> Saved
            </span>
          )}
          {update.isError && <span className="text-sm text-destructive">{authErrorMessage(update.error)}</span>}
        </div>
      )}
    </div>
  );
}
